import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CircleNotch, Coin, Clock, ArrowRight, Lock, Fire, Lightning, Lightbulb, Target,
} from "@phosphor-icons/react";

const MONTHLY_GOAL_USD = 1000;

function fmtMoney(byCurrency) {
  if (!byCurrency || Object.keys(byCurrency).length === 0) return "$0";
  return Object.entries(byCurrency)
    .map(([cur, v]) => `${cur === "USD" ? "$" : ""}${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}${cur !== "USD" ? " " + cur : ""}`)
    .join(" · ");
}

/** Concise earnings summary for the agent's own dashboard. */
export function AgentEarningsCard({ user }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/agent/commissions");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData({ error: true });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (data === null) {
    return (
      <div className="rounded-lg border border-white/10 bg-ink-surface p-5 mb-6 flex items-center justify-center min-h-[120px]" data-testid="agent-earnings-card">
        <CircleNotch size={20} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }
  if (data?.error) return null;

  const rows = data.rows || [];
  // Total tracked commissions (lifetime) — every row's expected_commission
  const total = {};
  // Pending payout (effective state = normal flow, i.e. not gated by lifecycle)
  const pendingPayout = {};
  // Held = lifecycle gate for pending agents
  const held = {};
  // Paid (already "Paid by Client" or "Commission Expected") in normal flow
  const paid = {};
  // iter35 — Monthly tracked commission (USD only — goal is in USD).
  // Counts EARNED commission for this calendar month: Paid + Pending + Held.
  // Excludes Blocked / Inactive / Reversed (commission is wiped or unearnable).
  // Date source: `commission_date` from the API — which is the booking date
  // (`booked_at`) of the parent itinerary, falling back to `approved_at` then
  // `created_at`. Previously this read `r.created_at` which the API never
  // returns, so the bucket was always $0 (Rechel bug).
  let monthlyEarnedUsd = 0;
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  rows.forEach((r) => {
    const cur = r.commission_currency || "USD";
    // Agent only earns their tier % of the supplier commission. Fall back to full
    // amount only if backend hasn't computed a payout yet (older row schema).
    const amt = parseFloat(r.agent_payout != null ? r.agent_payout : r.expected_commission) || 0;
    total[cur] = (total[cur] || 0) + amt;
    const eff = r.effective_status;
    if (eff === "Paid" || r.commission_paid) {
      paid[cur] = (paid[cur] || 0) + amt;
    } else if (eff === "Held") {
      held[cur] = (held[cur] || 0) + amt;
    } else if (eff === "Blocked" || eff === "Inactive" || eff === "Reversed") {
      // Hidden in agent display — not counted toward the monthly goal either
    } else {
      pendingPayout[cur] = (pendingPayout[cur] || 0) + amt;
    }
    // Monthly bucket: only USD rows, only this calendar month (YYYY-MM match),
    // and only rows whose commission is actually trackable (not wiped/blocked).
    // `commission_date` is an ISO date string like "2026-05-12" sourced from
    // the booking date of the parent itinerary.
    if (
      cur === "USD" &&
      typeof r.commission_date === "string" &&
      r.commission_date.startsWith(monthKey) &&
      eff !== "Blocked" &&
      eff !== "Inactive" &&
      eff !== "Reversed"
    ) {
      monthlyEarnedUsd += amt;
    }
  });

  const goal = user?.monthly_goal_usd || MONTHLY_GOAL_USD;
  const progressPct = Math.min(100, Math.round((monthlyEarnedUsd / goal) * 100));
  const monthlyText = `$${monthlyEarnedUsd.toLocaleString("en-US", { maximumFractionDigits: 0 })} / $${goal.toLocaleString("en-US")}`;
  const monthLabel = now.toLocaleString("en-US", { month: "long" });

  const isPending = user?.agent_status === "pending";
  const hasHeld = Object.keys(held).length > 0;

  return (
    <div className="rounded-lg border border-white/10 bg-ink-surface p-5 sm:p-6 mb-6" data-testid="agent-earnings-card">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-sans">Earnings snapshot</p>
        <Link
          to="/dashboard"
          onClick={(e) => e.preventDefault()}
          className="text-[10px] uppercase tracking-widest text-white/40 hover:text-[#D4AF37] cursor-default"
        >
          {rows.length} commission row{rows.length === 1 ? "" : "s"}
        </Link>
      </div>

      {/* Monthly goal progress — motivational money-mover */}
      <div className="mb-5 rounded-md border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/5 to-transparent p-4" data-testid="monthly-goal-progress">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Target size={14} weight="duotone" className="text-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">{monthLabel} goal</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-xl text-white" data-testid="monthly-goal-value">{monthlyText}</span>
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-sans">{progressPct}%</span>
          </div>
        </div>
        <div className="h-2.5 rounded-full bg-white/5 overflow-hidden border border-white/5">
          <div
            className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3C853] rounded-full transition-all duration-700 ease-out"
            style={{ width: `${progressPct}%` }}
            data-testid="monthly-goal-bar"
          />
        </div>
        <div className="mt-3 flex items-start gap-2" data-testid="monthly-goal-message">
          {monthlyEarnedUsd === 0 ? (
            <>
              <Fire size={14} weight="duotone" className="text-orange-400 shrink-0 mt-0.5" />
              <p className="text-sm text-white/80 font-sans">Close your first booking to start earning</p>
            </>
          ) : progressPct >= 100 ? (
            <>
              <Lightning size={14} weight="fill" className="text-[#D4AF37] shrink-0 mt-0.5" />
              <p className="text-sm text-white/90 font-sans">
                <span className="text-[#D4AF37] font-semibold">Goal smashed!</span> You're crushing it this month.
              </p>
            </>
          ) : (
            <>
              <Lightning size={14} weight="fill" className="text-[#D4AF37] shrink-0 mt-0.5" />
              <p className="text-sm text-white/80 font-sans">You're on your way — keep going</p>
            </>
          )}
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-white/40 font-sans" data-testid="monthly-goal-tip">
          <Lightbulb size={11} weight="duotone" /> Tracks commission earned this month (paid + pending). Average booking earns $150–$500.
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat
          icon={Coin}
          label="Total tracked"
          value={fmtMoney(total)}
          testId="agent-earnings-total"
        />
        <Stat
          icon={ArrowRight}
          label="Pending payout"
          value={fmtMoney(pendingPayout)}
          tone="emerald"
          testId="agent-earnings-pending"
        />
        {hasHeld ? (
          <Stat
            icon={Lock}
            label="Held"
            value={fmtMoney(held)}
            tone="amber"
            testId="agent-earnings-held"
          />
        ) : (
          <Stat
            icon={Clock}
            label="Paid (this period)"
            value={fmtMoney(paid)}
            tone="muted"
            testId="agent-earnings-paid"
          />
        )}
      </div>

      {isPending && hasHeld && (
        <p
          className="mt-4 text-xs text-amber-300 font-sans flex items-start gap-2 p-3 rounded-md bg-amber-500/5 border border-amber-400/20"
          data-testid="agent-earnings-held-message"
        >
          <Lock size={14} weight="duotone" className="mt-0.5 shrink-0" />
          <span>
            Your commissions are currently held. <strong className="text-amber-200">Complete onboarding</strong> and your admin will activate your account to unlock these earnings.
          </span>
        </p>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  const cls =
    tone === "emerald" ? "text-emerald-300"
    : tone === "amber" ? "text-amber-300"
    : tone === "muted" ? "text-white/50"
    : "text-[#D4AF37]";
  return (
    <div className="rounded-md border border-white/5 bg-black/30 p-3.5" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} weight="duotone" className={cls} />
        <p className={`text-[10px] uppercase tracking-[0.3em] font-sans ${cls}`}>{label}</p>
      </div>
      <p className="font-serif text-2xl text-white">{value}</p>
    </div>
  );
}
